// @ts-nocheck
import { type ApolloCache, type ApolloClient } from "@apollo/client";
import { produce, castDraft } from "immer";

import { GroupCache, GroupOperations, GroupsQueryServiceV2 } from "../api/cache";
import { groupDetailsFragment } from "../api/fragments";
import { type GroupDetailsFragment } from "../api/fragments/groupDetailsFragment.gql-generated";
import {
    type GetCachedGroupListV2Query,
    type GetGroupListV2Query,
    type GetGroupListV2QueryVariables,
    getCachedGroupListV2,
    getGroupListV2,
} from "../api/queries";

type GroupListGroup = GetGroupListV2Query["getGroupListV2"]["groupsWithConnections"][0];
type GroupListConnection = GroupListGroup["connections"][0];

export class GroupsRepositoryV2 {
    constructor(private apolloClient: ApolloClient<any>) {}

    static removeAllGroupsConnections(apolloClient: ApolloClient<any>) {
        const data = apolloClient.readQuery<GetCachedGroupListV2Query>({
            query: getCachedGroupListV2,
        });

        data.getGroupListV2.groupsWithConnections.forEach((group) => {
            GroupCache.removeAllConnections(apolloClient.cache, group.group.id);
        });
    }

    static initDefaults(cache: ApolloCache<any>) {
        cache.writeQuery<Partial<GetCachedGroupListV2Query>>({
            query: getCachedGroupListV2,
            data: {
                __typename: "QueryType",
                getGroupListV2: {
                    groupsWithConnections: [],
                    __typename: "GroupListWithConnections",
                },
            },
        });
    }

    loadGroupsToCache(variables: GetGroupListV2QueryVariables) {
        return this.apolloClient
            .query<GetGroupListV2Query, GetGroupListV2QueryVariables>({
                query: getGroupListV2,
                variables,
                fetchPolicy: "no-cache",
            })
            .then(({ data }) => {
                this.appendToCache(data);
                return data.getGroupListV2.groupsWithConnections;
            });
    }

    appendToCache(groups: GetGroupListV2Query) {
        if (!groups || groups.getGroupListV2?.groupsWithConnections?.length === 0) {
            return;
        }

        const cachedGroups = this.apolloClient.readQuery<GetCachedGroupListV2Query>({
            query: getCachedGroupListV2,
        });

        const mergedGroupsConnections = groups.getGroupListV2?.groupsWithConnections?.map((groupConnections) =>
            this.mergeGroupConnections(groupConnections),
        );

        const mergedGroups = produce(groups, (groupsDraft) => {
            groupsDraft.getGroupListV2.groupsWithConnections = castDraft(mergedGroupsConnections);
        });

        this.apolloClient.cache.writeQuery({
            query: getCachedGroupListV2,
            data: GroupsQueryServiceV2.concat(cachedGroups, mergedGroups),
        });
    }

    private getGroup(groupId: string) {
        return this.apolloClient.readFragment<GroupDetailsFragment>({
            fragment: groupDetailsFragment,
            id: GroupCache.createGroupWithConnectionsId(groupId),
            fragmentName: "GroupDetails",
        });
    }

    private mergeGroupConnections(groupConnections: GroupListGroup): GroupListGroup {
        const groupDetails = this.getGroup(groupConnections.group.id);

        return groupDetails
            ? GroupOperations.setConnections<GroupListConnection, GroupListGroup>(
                  groupConnections,
                  groupDetails?.connections,
              )
            : groupConnections;
    }
}
